import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { i18n } from "@/config/i18n";
import { useI18n } from "vue-i18n";
import {
  getSafetyFactorOptions,
  VIEW_FACTORS_WITHOUT_GRADIENT,
} from "@/pages/safety-factor/config";

import { getUncertaintiesWithColors, soilTypeColors } from "@/colorLegends";
import { unitsConverter } from "@/config/units";
import {
  findMainUncertainty,
  getSafetyLevel,
} from "@/pages/safety-factor/helpers";
import { SafetyFactorWidget } from "@/pages/safety-factor/views/SafetyFactorMapmode/config";
import {
  getMinMaxValue,
  rangeFiltering,
  valueFiltering,
} from "@/utils/filterHelpers";
import { usePipePartsStore } from "./usePipePartsStore";
import { usePipelinesStore } from "./usePipelinesStore";
import { mergeChanges } from "@/utils/objects";
import type { PlotlyFilter } from "@/types/plotlyFilter";
import type {
  SafetyFactorFilters,
  SafetyLevel,
  SelectedViewFactor,
} from "@/types/safetyFactor";
import { useStoreUnconfirmedFilters } from "@/utils/storeHelpers";
import type { WidgetsSettingsType } from "@/types/widgets";
import { shouldBeWidgetsInitMinimized } from "@/config/widgets";

const requests = new Map();

const INITIAL_PLOTLY_FILTER: PlotlyFilter = { property: "", range: [] };

const INITIAL_SAFETY_FACTOR_FILTERS: SafetyFactorFilters = {
  selectedMainUncertainties: [],
  selectedSafetyRange: [],
  selectedLowerSafetyRange: [],
  selectedUpperSafetyRange: [],
  plotlyFilter: INITIAL_PLOTLY_FILTER,
};

export const useSafetyFactorStore = defineStore("safetyFactor", () => {
  const pipePartsStore = usePipePartsStore();
  const pipelinesStore = usePipelinesStore();

  const { t } = useI18n();

  const shouldGetNewSafetyFactor = ref(true);

  watch(
    () => pipelinesStore.selectedInspectionId,
    () => {
      shouldGetNewSafetyFactor.value = true;
    }
  );

  const isLoading = ref(false);

  function setLoading(loading: boolean) {
    isLoading.value = loading;
  }

  const selectedPipePartId = ref<string | null>(null);

  const selectedPipePart = computed(() => {
    if (!selectedPipePartId.value) return;

    return pipePartsStore.pipeParts.find(
      ({ id }) => id === selectedPipePartId.value
    );
  });

  function setSelectedPipePartId(pipePartId: string) {
    selectedPipePartId.value = pipePartId;
  }

  const filters = ref(structuredClone(INITIAL_SAFETY_FACTOR_FILTERS));

  const hasActiveFilters = computed(() => {
    if (filters.value.selectedMainUncertainties.length) return true;

    return Object.entries(filters.value).some(([key, value]) => {
      const rangeMap = {
        selectedSafetyRange: minMaxSafetyRange.value,
        selectedLowerSafetyRange: minMaxLowerSafetyRange.value,
        selectedUpperSafetyRange: minMaxUpperSafetyRange.value,
      };

      if (key in rangeMap) {
        const [min, max] = rangeMap[key];
        return min !== value[0] || max !== value[1];
      }
    });
  });
  const filteredPipeParts = computed(() => {
    const {
      plotlyFilter,
      selectedSafetyRange,
      selectedLowerSafetyRange,
      selectedUpperSafetyRange,
      selectedMainUncertainties,
    } = filters.value;
    const uncertaintiesWithColors = getUncertaintiesWithColors(i18n.global.t);

    let filtered = pipePartsStore.pipeParts.map((part) => {
      const converted = {
        ...part,
        safety_factor_calculation: { ...part.safety_factor_calculation },
      };

      converted.distance = unitsConverter.instance.convert(
        converted.distance,
        "m"
      );
      converted.length = unitsConverter.instance.convert(converted.length, "m");

      if (!converted.safety_factor_calculation) {
        return converted;
      }

      [
        "ground_level_mean",
        "ground_level_std",
        "ground_water_level_min",
        "ground_water_level_max",
        "road_distance_mean",
        "road_distance_std",
      ].forEach((key) => {
        converted.safety_factor_calculation[key] =
          unitsConverter.instance.convert(
            converted.safety_factor_calculation[key],
            "m"
          );
      });

      const mainUncertainty = findMainUncertainty(converted);

      converted.safety_factor_calculation.main_uncertainty =
        uncertaintiesWithColors[mainUncertainty];

      return converted;
    });

    filtered = rangeFiltering(
      filtered,
      // @ts-expect-error
      "safety_factor_calculation.safety_factor",
      selectedSafetyRange
    );

    filtered = rangeFiltering(
      filtered,
      // @ts-expect-error
      "safety_factor_calculation.safety_factor_lower",
      selectedLowerSafetyRange
    );

    filtered = rangeFiltering(
      filtered,
      // @ts-expect-error
      "safety_factor_calculation.safety_factor_upper",
      selectedUpperSafetyRange
    );

    filtered = valueFiltering(
      filtered,
      "safety_factor_calculation.main_uncertainty.text",
      selectedMainUncertainties
    );

    if (plotlyFilter.property !== "") {
      const { property, range } = plotlyFilter;

      // histogram zoom only keeps pipe parts that have a value in the zoomed range
      filtered = rangeFiltering(
        filtered,
        // @ts-expect-error
        "safety_factor_calculation." + property,
        range,
        false
      );
    }

    return filtered;
  });

  function setFilters(newFilters) {
    mergeChanges(filters.value, newFilters);
    selectedPipePartId.value = null;
  }
  function initFilters() {
    setFilters({
      selectedSafetyRange: minMaxSafetyRange.value,
      selectedLowerSafetyRange: minMaxLowerSafetyRange.value,
      selectedUpperSafetyRange: minMaxUpperSafetyRange.value,
    });
  }
  function resetPlotlyFilter() {
    setFilters({ plotlyFilter: INITIAL_PLOTLY_FILTER });
  }
  function resetFilters() {
    filters.value = structuredClone(INITIAL_SAFETY_FACTOR_FILTERS);
    initFilters();
  }

  const selectedViewFactor = ref<SelectedViewFactor>(
    getSafetyFactorOptions(i18n.global.t)[1].value
  );

  const gradientMinMaxValues = computed(() => {
    if (VIEW_FACTORS_WITHOUT_GRADIENT.includes(selectedViewFactor.value)) {
      return [];
    }

    return getMinMaxValue(
      filteredPipeParts.value,
      // @ts-expect-error
      "safety_factor_calculation." + selectedViewFactor.value
    );
  });
  const uniqueSafetyLevels = computed(() => {
    if (
      !["safety_factor", "safety_factor_lower", "safety_factor_upper"].includes(
        selectedViewFactor.value
      )
    ) {
      return [];
    }

    const uniqueList: Set<SafetyLevel> = new Set();

    pipePartsStore.pipeParts.forEach((part) => {
      if (part.safety_factor_calculation) {
        const level = getSafetyLevel(
          part.safety_factor_calculation[selectedViewFactor.value]
        );

        uniqueList.add(level);
      }
    });

    const orderedLevelsList = Array.from(uniqueList).sort((a, b) =>
      a.index > b.index ? 1 : -1
    );

    return orderedLevelsList;
  });
  const legendList = computed(() => {
    if (selectedViewFactor.value.includes("safety_factor")) {
      return uniqueSafetyLevels.value;
    }

    if (selectedViewFactor.value === "main_contributor") {
      return uniqueUncertaintiesOptions.value;
    }

    if (selectedViewFactor.value === "soil_type") {
      return uniqueSoilTypes.value;
    }

    return [];
  });

  function setSelectedViewFactor(viewFactor: SelectedViewFactor) {
    selectedViewFactor.value = viewFactor;
  }

  // TODO add type for selectedLegend
  const selectedLegend = ref(null);

  function setSelectedLegend(legend) {
    selectedLegend.value = legend;
  }
  function toggleSelectedLegeng(legend) {
    if (selectedLegend.value === legend) {
      setSelectedLegend(null);
      return;
    }

    setSelectedLegend(legend);
  }

  const minMaxSafetyRange = computed(() =>
    getMinMaxValue(
      pipePartsStore.pipeParts,
      // @ts-expect-error
      "safety_factor_calculation.safety_factor",
      true
    )
  );
  const minMaxLowerSafetyRange = computed(() =>
    getMinMaxValue(
      pipePartsStore.pipeParts,
      // @ts-expect-error
      "safety_factor_calculation.safety_factor_lower",
      true
    )
  );
  const minMaxUpperSafetyRange = computed(() =>
    getMinMaxValue(
      pipePartsStore.pipeParts,
      // @ts-expect-error
      "safety_factor_calculation.safety_factor_upper",
      true
    )
  );
  const uniqueUncertaintiesOptions = computed(() => {
    const allMainUncertainties = pipePartsStore.pipeParts.map((part) =>
      findMainUncertainty(part)
    );

    const uniqueList = Array.from(new Set(allMainUncertainties));
    const uncertaintiesWithColors = getUncertaintiesWithColors(i18n.global.t);

    return uniqueList.map((key) => uncertaintiesWithColors[key]);
  });
  const uniqueSoilTypes = computed(() => {
    const uniqueList = new Set();

    pipePartsStore.pipeParts.forEach((part) => {
      if (part.safety_factor_calculation) {
        uniqueList.add(part.safety_factor_calculation.soil_type);
      }
    });

    const uniqueSoilTypesWithColors = Array.from(uniqueList).map(
      (type) => soilTypeColors[type]
    );

    return uniqueSoilTypesWithColors;
  });

  const wmsLayers = ref([]);
  const safetyFactorOptions = computed(() => getSafetyFactorOptions(t));
  const safetyChartTitle = computed(
    () =>
      safetyFactorOptions.value.find(
        (opt) => opt.value === selectedViewFactor.value
      )?.title || ""
  );
  const showGradient = computed(
    () => !VIEW_FACTORS_WITHOUT_GRADIENT.includes(selectedViewFactor.value)
  );
  const legendTitle = computed(() => {
    let translationKey;

    switch (selectedViewFactor.value) {
      case "main_contributor":
        translationKey = "safety_factor.widgets.uncertainty_legend";
        break;
      case "soil_type":
        translationKey = "safety_factor.widgets.soil_type_legend";
        break;
      case "safety_factor":
      case "safety_factor_lower":
      case "safety_factor_upper":
        translationKey = "safety_factor.widgets.safety_level_legend";
        break;
      default:
        return "";
    }

    return t(translationKey);
  });

  const widgetsMapSettings = ref<WidgetsSettingsType>({
    [SafetyFactorWidget.PipePartsTable]: {
      key: SafetyFactorWidget.PipePartsTable,
      title: t("safety_factor.widgets.table"),
      initialWidth: 840,
      initialHeight: 355,
      getInitialX: (container, widgets) =>
        container.offsetWidth -
        (widgets[SafetyFactorWidget.PipePartsTable].initialWidth + 10),
      getInitialY: () => 175,
      initiallyMinimized: shouldBeWidgetsInitMinimized,
    },

    [SafetyFactorWidget.SafetyChart]: {
      key: SafetyFactorWidget.SafetyChart,
      title: safetyChartTitle,
      initialWidth: 655,
      initialHeight: 400,
      getInitialX: () => 5,
      getInitialY: (container, widgets) =>
        container.offsetHeight -
        (widgets[SafetyFactorWidget.SafetyChart].initialHeight + 35),
      initiallyMinimized: shouldBeWidgetsInitMinimized,
    },

    [SafetyFactorWidget.ContributionsChart]: {
      key: SafetyFactorWidget.ContributionsChart,
      title: t("safety_factor.widgets.contributions_chart"),
      initialWidth: 655,
      initialHeight: 400,
      getInitialX: (container, widgets) =>
        container.offsetWidth -
        (widgets[SafetyFactorWidget.ContributionsChart].initialWidth + 30),
      getInitialY: (container, widgets) =>
        container.offsetHeight -
        (widgets[SafetyFactorWidget.ContributionsChart].initialHeight + 35),
      initiallyMinimized: true,
    },

    [SafetyFactorWidget.HeightProfileChart]: {
      key: SafetyFactorWidget.HeightProfileChart,
      title: t("safety_factor.widgets.height_profile"),
      initialWidth: 655,
      initialHeight: 400,
      getInitialX: () => 5,
      getInitialY: (container, widgets) =>
        container.offsetHeight -
        (widgets[SafetyFactorWidget.HeightProfileChart].initialHeight + 180),
      initiallyMinimized: true,
    },

    [SafetyFactorWidget.GradientLegend]: {
      key: SafetyFactorWidget.GradientLegend,
      title: t("joints.widgets.gradient.maximize_title"),
      initialWidth: 130,
      initialHeight: "auto",
      getInitialX: () => 5,
      getInitialY: () => 50,
      initiallyMinimized: false,
      hidden: computed(() => !showGradient.value),
      resizable: false,
      draggable: false,
      minimizable: false,
    },

    [SafetyFactorWidget.ColorsLegend]: {
      key: SafetyFactorWidget.ColorsLegend,
      title: legendTitle,
      initialWidth: 180,
      initialHeight: "auto",
      getInitialX: () => 5,
      getInitialY: () => 50,
      initiallyMinimized: false,
      hidden: computed(() => showGradient.value),
      resizable: false,
      draggable: false,
      minimizable: false,
    },

    [SafetyFactorWidget.WMSWidget]: {
      key: SafetyFactorWidget.WMSWidget,
      title: t("dashboard.widgets.color.maximize_title"),
      initialWidth: 350,
      initialHeight: "auto",
      getInitialX: (container, widgets) =>
        (container.offsetWidth -
          widgets[SafetyFactorWidget.WMSWidget].initialWidth) /
        2,
      getInitialY: () => 5,
      initiallyMinimized: false,
      resizable: false,
    },

    [SafetyFactorWidget.WMSLegend]: {
      key: SafetyFactorWidget.WMSLegend,
      title: t("wms_layers.layers"),
      initialWidth: 250,
      initialHeight: 365,
      getInitialX: () => 5,
      getInitialY: () => 180,
      initiallyMinimized: false,
      hidden: wmsLayers.value.length === 0,
    },
  });
  function updateWidgetsMapSettings(
    newSettings: typeof widgetsMapSettings.value
  ) {
    // assign per key so computed settings (title, hidden) stay reactive
    Object.entries(newSettings).forEach(([widgetName, widgetNewSettings]) => {
      Object.keys(widgetNewSettings).forEach((key) => {
        widgetsMapSettings.value[widgetName][key] = widgetNewSettings[key];
      });
    });
  }

  async function initSafetyFactor() {
    setLoading(true);
    await pipePartsStore.getPipeParts();
    initFilters();
    requests.delete("pipePartsRequest");
    setLoading(false);
    shouldGetNewSafetyFactor.value = false;
  }
  function cancelAllRequests() {
    for (const request of requests.values()) {
      request.cancel();
    }
  }

  const {
    unconfirmedFilters,
    setUnconfirmedFilters,
    confirmUnconfirmedFilters,
    areNewFiltersUnconfirmed,
  } = useStoreUnconfirmedFilters(filters);

  return {
    shouldGetNewSafetyFactor,
    isLoading,
    selectedPipePartId,
    filters,
    selectedViewFactor,
    selectedLegend,
    minMaxSafetyRange,
    minMaxLowerSafetyRange,
    minMaxUpperSafetyRange,
    uniqueUncertaintiesOptions,
    uniqueSoilTypes,
    gradientMinMaxValues,
    uniqueSafetyLevels,
    legendList,
    selectedPipePart,
    hasActiveFilters,
    filteredPipeParts,
    setLoading,
    setSelectedPipePartId,
    setFilters,
    initFilters,
    resetPlotlyFilter,
    resetFilters,
    setSelectedViewFactor,
    setSelectedLegend,
    toggleSelectedLegeng,
    safetyFactorOptions,
    safetyChartTitle,
    legendTitle,
    wmsLayers,
    widgetsMapSettings,
    updateWidgetsMapSettings,
    initSafetyFactor,
    cancelAllRequests,
    unconfirmedFilters,
    setUnconfirmedFilters,
    confirmUnconfirmedFilters,
    areNewFiltersUnconfirmed,
  };
});
